import React, { useMemo, useState } from "react";
import { Checkbox, Popover, Tooltip } from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import CollectionsRoundedIcon from "@mui/icons-material/CollectionsRounded";
import EventNoteRoundedIcon from "@mui/icons-material/EventNoteRounded";
import InboxRoundedIcon from "@mui/icons-material/InboxRounded";
import FilterAltOffRoundedIcon from "@mui/icons-material/FilterAltOffRounded";
import SelectMenuItems from "../SelectMenuItems";
import "./backlogtable.css";
import { useDispatch, useSelector } from "react-redux";
import { getSprintsList, getBacklogIssues, getLastFiveActiveSprintInfo, SetlastfiveActiveSprintInfo } from "../../common/Redux/Reducer";
import CreateBacklogIssue from "../Create_Backlogissue";
import axios from "axios";
import API_BASE_URL from "../config/appConfig";
import SearchableDropdown from "../Modal/SearchableDropDown";
import { Draggable } from "react-beautiful-dnd";
import { jwtDecode } from "jwt-decode";
import { Glyph, PersonAvatar, getPriorityMeta, getStatusMeta, getTypeMeta } from "../Modal/TaskWorkFlowParts";
import { useToast } from "../Toast";
import { useUserPhotos } from "../../utilities/userPhotos";

/* Descriptions are rich HTML; a row shows one line of their text and how many
   images they carry, rather than rendering the images inline. */
const describe = (html = "") => {
  if (!html) return { text: "", images: 0 };
  const images = (String(html).match(/<img\b/gi) || []).length;
  const text = String(html)
    .replace(/<img\b[^>]*>/gi, " ")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/\s+/g, " ")
    .trim();
  return { text, images };
};

const currentUserId = () => {
  try {
    const token = localStorage.getItem("accessToken");
    return token ? jwtDecode(token)?.id : undefined;
  } catch (error) {
    return undefined;
  }
};

const BacklogTable = ({
  data = [],
  sprint = null,
  handleIssueClick = () => {},
  handleSprintIssueClick = () => {},
  issuesSelected = [],
  setIssuesSelected = () => {},
  searchTerm,
  selectedIssueType = "",
  selectPriorityType = "",
  selectedEpic = "",
}) => {
  const [isCreateNewBackIssuse, setIsCreateNewBackIssue] = useState(false);
  const [assigneeAnchor, setAssigneeAnchor] = useState(null);
  const [assigneeRow, setAssigneeRow] = useState(null);
  const dispatch = useDispatch();
  const toast = useToast();
  /* an issue only stores the assignee's name and id, so the photo is looked up */
  const photoOf = useUserPhotos();
  const filterAssigneesArray = useSelector((state) => state.ProjamAppSlice.assignees);
  const { Projects, assignees } = useSelector((state) => state.ProjamAppSlice);
  const contributors = Projects?.selectedProject?.contributors;
  const issueStatus = Projects?.selectedProject?.selectedProjectData?.IssueStatus;

  let backlogId = Projects?.selectedProject?.selectedProjectBackLogId;
  let projectId = Projects?.selectedProject?.selectedProjectData?._id;

  const statusItems = useMemo(
    () =>
      (Array.isArray(issueStatus) ? issueStatus : []).map((status) => ({
        ...status,
        icon: <Glyph meta={getStatusMeta(status?.title || status?.name)} />,
      })),
    [issueStatus]
  );

  const handleIssueSelected = (e, id) => {
    const isChecked = e.target.checked;
    setIssuesSelected((previous) => {
      const withoutId = previous.filter((issueId) => issueId !== id);
      return isChecked ? [...withoutId, id] : withoutId;
    });
  };

  const openRow = (row) => {
    if (sprint) {
      handleSprintIssueClick(row);
    } else {
      handleIssueClick(row);
    }
  };

  const refresh = () => {
    dispatch(getSprintsList({ backlogId: backlogId, filterAssigneesArray: filterAssigneesArray }));
    dispatch(getBacklogIssues(backlogId, filterAssigneesArray));
  };

  const handleStatuUpdate = async (issueId, currentStatus, newStatus) => {
    if (currentStatus === newStatus) {
      return;
    }
    try {
      await axios.put(`${API_BASE_URL}/issues/updateIssue/${issueId}?userid=${currentUserId()}`, { status: newStatus });
      if (newStatus === "Done") {
        let response = dispatch(getLastFiveActiveSprintInfo({ backlogId, projectId }));
        dispatch(SetlastfiveActiveSprintInfo(response));
      }
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Could not update the status");
    } finally {
      refresh();
    }
  };

  const closeAssignee = () => {
    setAssigneeAnchor(null);
  };

  const handelAssigneUpdate = async (issueId, empledata) => {
    const currentAssignee = data.find((issue) => issue._id === issueId)?.assignee?.[0]?.employeeName;
    if (currentAssignee === empledata?.employeeName) {
      closeAssignee();
      return;
    }
    try {
      await axios.put(`${API_BASE_URL}/issues/updateIssue/${issueId}?userid=${currentUserId()}`, {
        assignee: empledata ? [empledata] : [],
      });
    } catch (error) {
      console.log("error", error);
      toast.error("Could not update the assignee");
    } finally {
      refresh();
      closeAssignee();
    }
  };

  const filteredData = useMemo(() => {
    let list = Array.isArray(data) ? data : [];
    if (selectedEpic) {
      return list.filter(
        (eachIssue) =>
          eachIssue?.name?.toLowerCase() === selectedEpic?.toLowerCase() || eachIssue?.title?.toLowerCase() === selectedEpic?.toLowerCase()
      );
    }
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      list = list.filter((item) => item?.name?.toLowerCase().includes(term) || item?.summary?.toLowerCase().includes(term));
    }
    if (selectedIssueType) {
      list = list.filter((eachIssue) => eachIssue?.issueType?.toLowerCase() === selectedIssueType?.toLowerCase());
    }
    if (selectPriorityType) {
      list = list.filter((eachIssue) => eachIssue?.priority?.toLowerCase() === selectPriorityType?.toLowerCase());
    }
    if (assignees?.length) {
      list = list.filter((issue) => assignees.some((person) => issue?.assignee?.[0]?.employeeName === person));
    }
    return list;
  }, [searchTerm, data, selectedIssueType, selectPriorityType, selectedEpic, assignees]);

  const isFiltering = Boolean(searchTerm || selectedIssueType || selectPriorityType || selectedEpic || assignees?.length);
  const hasData = Array.isArray(data) && data.length > 0;

  return (
    <div className="bl-table">
      {filteredData.length ? (
        <div className="bl-rows">
          {filteredData.map((row, index) => (
            <Draggable key={row?._id} draggableId={row?._id || `issue-${index}`} index={index}>
              {(provided, snapshot) => {
                const typeMeta = getTypeMeta(row?.issueType);
                const priorityMeta = getPriorityMeta(row?.priority);
                const statusMeta = getStatusMeta(row?.status);
                const owner = row?.assignee?.[0];
                const ownerName = owner?.employeeName || owner?.employeeEmail || "";
                const { text, images } = describe(row?.description);
                const isSelected = issuesSelected.includes(row?._id);

                return (
                  <div
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    style={provided.draggableProps.style}
                    className={`bl-row ${snapshot.isDragging ? "is-dragging" : ""} ${isSelected ? "is-selected" : ""}`}
                  >
                    <Checkbox
                      size="small"
                      className="bl-row__check"
                      checked={isSelected}
                      disabled={row?.status === "Done"}
                      onChange={(e) => handleIssueSelected(e, row?._id)}
                      onClick={(e) => e.stopPropagation()}
                      inputProps={{ "aria-label": `Select ${row?.name}` }}
                    />
                    <Tooltip title={row?.issueType || "Story"}>
                      <span className={`bl-row__type pj-tone--${typeMeta.tone}`}>
                        <Glyph meta={typeMeta} />
                      </span>
                    </Tooltip>

                    <button type="button" className="bl-row__main" onClick={() => openRow(row)}>
                      <span className="bl-row__line">
                        <span className="bl-row__key">{row?.name}</span>
                        <span className="bl-row__summary">{row?.summary}</span>
                        {row?.titleMsg ? <span className="bl-row__msg">{row.titleMsg}</span> : null}
                      </span>
                      {text || images ? (
                        <span className="bl-row__desc">
                          {text ? <span className="bl-row__desc-text">{text}</span> : null}
                          {images ? (
                            <span className="bl-row__images">
                              <CollectionsRoundedIcon />
                              {images}
                            </span>
                          ) : null}
                        </span>
                      ) : null}
                    </button>

                    <div className="bl-row__side">
                      {row?.meetings ? <span className="bl-row__tag">Meetings</span> : null}
                      <div className="bl-row__status" onClick={(e) => e.stopPropagation()}>
                        <SelectMenuItems
                          title={row?.status}
                          backGroundColor=""
                          icon={<Glyph meta={statusMeta} />}
                          ItemsList={statusItems}
                          RequiredFontSize="12.5px"
                          MenuItemFontSize="13px"
                          showHeader={false}
                          className={`bl-status-trigger pj-tone--${statusMeta.tone}`}
                          menuClassName="bl-menu"
                          handleStatuUpdate={(value) => handleStatuUpdate(row?._id, row?.status, value)}
                        />
                      </div>
                      <Tooltip title={`Priority: ${row?.priority || "Medium"}`}>
                        <span className={`bl-row__priority pj-tone--${priorityMeta.tone}`}>
                          <Glyph meta={priorityMeta} />
                        </span>
                      </Tooltip>
                      <Tooltip title={ownerName || "Unassigned"}>
                        <button
                          type="button"
                          className="bl-row__owner"
                          aria-label={`Assignee for ${row?.name}: ${ownerName || "Unassigned"}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setAssigneeRow(row);
                            setAssigneeAnchor(e.currentTarget);
                          }}
                        >
                          <PersonAvatar name={ownerName} src={photoOf(owner)} size={28} />
                        </button>
                      </Tooltip>
                    </div>
                  </div>
                );
              }}
            </Draggable>
          ))}
        </div>
      ) : (
        <div className="bl-empty">
          <span className="bl-empty__icon">
            {isFiltering && hasData ? <FilterAltOffRoundedIcon /> : sprint ? <EventNoteRoundedIcon /> : <InboxRoundedIcon />}
          </span>
          <strong>{isFiltering && hasData ? "No issues match your filters" : sprint ? "Plan this sprint" : "The backlog is clear"}</strong>
          <span>
            {isFiltering && hasData
              ? "Try another search or clear the filters."
              : sprint
                ? "Drag issues here or create one. Select Start sprint when you're ready."
                : "New issues land here until they're planned into a sprint."}
          </span>
        </div>
      )}

      <button type="button" className="bl-create" onClick={() => setIsCreateNewBackIssue(true)}>
        <span className="bl-create__plus">
          <AddRoundedIcon />
        </span>
        Create issue
      </button>

      {/* one assignee picker for the whole list, pointed at the row that opened it */}
      <Popover
        open={Boolean(assigneeAnchor)}
        anchorEl={assigneeAnchor}
        onClose={closeAssignee}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { className: "bl-popover bl-popover--people" } }}
      >
        {assigneeRow ? (
          <div className="bl-people">
            <span className="bl-people__title">Assign {assigneeRow?.name}</span>
            <SearchableDropdown
              workFlowModalTaskData={assigneeRow}
              members={contributors || []}
              handleAssigneeUpdate={handelAssigneUpdate}
              currentRowId={assigneeRow?._id}
            />
          </div>
        ) : null}
      </Popover>

      {isCreateNewBackIssuse ? (
        <CreateBacklogIssue isCreateNewBackIssuse={isCreateNewBackIssuse} setIsCreateNewBackIssue={setIsCreateNewBackIssue} sprint={sprint} />
      ) : null}
    </div>
  );
};

export default BacklogTable;
